import { createContext, useEffect, useState } from 'react';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { FeatureFlags } from 'featureFlags/constants/featureFlags';
import { useFeatureFlag } from 'shared/hooks';
import getConfig from 'next/config';

declare global {
  interface Window {
    OnetrustActiveGroups?: string;
  }
}

const BRANCH_BANNER_HEIGHT = '82px';

export const BranchSdkContext = createContext(null);

export const BranchSdkProvider = ({ children, cspNonce }) => {
  const {
    publicRuntimeConfig: { BRANCH_SDK_PUBLIC_KEY },
  } = getConfig();
  const isBranchBannerEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_BRANCH_SDK_BANNER);
  const isOneTrustEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_ONE_TRUST);
  const [isBranchBannerActive, setBranchBannerActive] = useState(false);
  const [branchSdk, setBranchSdk] = useState(null);
  const initBranchSdk = async (disableTracking: boolean) => {
    try {
      const sdk = (await import('branch-sdk')).default;
      const options = { nonce: cspNonce, tracking_disabled: disableTracking };
      sdk.init(BRANCH_SDK_PUBLIC_KEY, options, (err: string) => {
        if (err) {
          bugsnag(err, { context: 'Branch.io SDK initialization.' });
        }
      });
      sdk.addListener('didCloseJourney', () => {
        setBranchBannerActive(false);
      });
      sdk.addListener('didShowJourney', () => {
        setBranchBannerActive(true);
      });

      return sdk;
    } catch (error) {
      bugsnag(error, { context: 'Init Branch.io SDK' });
    }
  };

  const startBranchJourney = async (disableTracking = false) => {
    try {
      const sdk = await initBranchSdk(disableTracking);
      if (document && sdk) {
        // any click action in the app triggers banner dismissal
        document.addEventListener('click', () => {
          sdk.closeJourney();
        });
      }
      setBranchSdk(sdk);
    } catch (error) {
      bugsnag(error, { context: 'Start Branch.io SDK journey' });
    }
  };

  useEffect(() => {
    if (isBranchBannerEnabled) {
      const disableTracking =
        isOneTrustEnabled &&
        window.OnetrustActiveGroups &&
        !window.OnetrustActiveGroups.includes('C0004');
      void startBranchJourney(disableTracking);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (window.OneTrust) {
        window.OneTrust.OnConsentChanged(() => {
          if (isBranchBannerEnabled && window.OnetrustActiveGroups && isOneTrustEnabled) {
            const disableTracking = !window.OnetrustActiveGroups.includes('C0004');
            if (branchSdk) {
              branchSdk.disableTracking(disableTracking);
            }
          }
        });
      }
    }, 3000);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchSdk]);

  return (
    <BranchSdkContext.Provider
      value={{ branchSdk, isBranchBannerActive, branchBannerHeight: BRANCH_BANNER_HEIGHT }}
    >
      {children}
    </BranchSdkContext.Provider>
  );
};
